import {
  CONTROL_KEYS,
  type ControlKey,
  INSTRUMENT_BATCH_TYPE_LABELS,
  INSTRUMENT_KIND_LABELS,
  INSTRUMENT_STATUS_LABELS,
  type InstrumentBatch,
} from "@station/contracts";
import { Alert, Button, Card, MoneyText, SkeletonRows, StatusBadge, useToast } from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Ban, FileDown } from "lucide-react";
import { type ReactElement, type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import {
  downloadReportFile,
  ReasonDialog,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import { batchParty, recalledBatchResult } from "../../../../features/instruments/instrument-values.js";
import { useInstrumentBatch } from "../../../../features/instruments/queries.js";
import {
  InstrumentPage,
  InstrumentWarnings,
  useInstrumentCrumb,
  useInstrumentPermissions,
  useRefreshInstruments,
} from "../../../../features/instruments/shared.js";
import { LedgerStatusBadge } from "../../../../features/treasury/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/cek-senet/bordrolar/$id")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: BatchDetailPage,
});

function BatchDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useInstrumentCrumb("/cek-senet/bordrolar");
  const detail = useInstrumentBatch(id);
  const batch = detail.data;
  const title = batch ? batch.batchNo : t("instruments.batch.title");
  return (
    <InstrumentPage
      title={title}
      {...(batch ? { description: INSTRUMENT_BATCH_TYPE_LABELS[batch.batchType] } : {})}
      breadcrumb={[crumb, { label: title }]}
      actions={batch ? <BatchActions batch={batch} /> : null}
    >
      {() =>
        detail.error ? (
          <Alert tone="danger">{errorMessage(detail.error)}</Alert>
        ) : !batch ? (
          <SkeletonRows rows={8} />
        ) : (
          <BatchBody batch={batch} />
        )
      }
    </InstrumentPage>
  );
}

/** PDF indir (giriş bordrosu müşteriye makbuz olarak verilir) ve bordro iptali (yalnızca çeklerin SON işlemiyse). */
function BatchActions({ batch }: { batch: InstrumentBatch }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInstruments();
  const canCancel = useInstrumentPermissions().canCancelBatch;
  const [cancelling, setCancelling] = useState(false);
  const pdf = useMutation({
    mutationFn: async () =>
      (await api.GET("/instrument-batches/{id}/pdf", { params: { path: { id: batch.id } } })).data!,
    onSuccess: downloadReportFile,
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  return (
    <>
      <Button variant="outline" loading={pdf.isPending} onClick={() => pdf.mutate()}>
        <FileDown aria-hidden="true" />
        {t("instruments.batch.pdf")}
      </Button>
      {canCancel && batch.status === "POSTED" ? (
        <Button variant="destructive" onClick={() => setCancelling(true)}>
          <Ban aria-hidden="true" />
          {t("instruments.batch.cancel")}
        </Button>
      ) : null}
      <ReasonDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("instruments.batch.cancelTitle", { no: batch.batchNo })}
        description={t("instruments.batch.cancelDescription")}
        confirmLabel={t("instruments.batch.cancel")}
        onConfirm={async (reason) => {
          // Sonraki bir bordro çeki taşıdıysa sunucu 409 INVALID_STATE_TRANSITION döner; ileti diyalogda görünür.
          await api.POST("/instrument-batches/{id}/cancel", {
            params: { path: { id: batch.id } },
            body: { reason, version: batch.version },
          });
          await refresh(batch.companyId);
          toast({ tone: "success", title: t("instruments.batch.cancelled", { no: batch.batchNo }) });
        }}
      />
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{children}</dd>
    </div>
  );
}

function BatchBody({ batch }: { batch: InstrumentBatch }): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const created = recalledBatchResult(batch.id);
  return (
    <div className="grid gap-4">
      {created ? (
        <>
          <Alert tone="success" title={t("instruments.batch.createdTitle", { no: batch.batchNo })}>
            {created.unallocatedAmount && created.unallocatedAmount !== "0" ? (
              <span>
                {t("instruments.batch.unallocated")} <MoneyText value={created.unallocatedAmount} />
              </span>
            ) : null}
          </Alert>
          <InstrumentWarnings warnings={created.warnings} />
        </>
      ) : null}
      {batch.status === "CANCELLED" ? (
        <Alert tone="warning" title={t("instruments.batch.cancelledTitle")}>
          {batch.cancelReason}
          {batch.cancelledAt ? ` · ${formatDateTime(batch.cancelledAt)}` : ""}
        </Alert>
      ) : null}

      <Card className="grid gap-3 p-4 sm:p-5">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("instruments.fields.date")}>
            <span className="tabular">{formatDate(batch.batchDate)}</span>
          </Field>
          <Field label={t("instruments.batches.type")}>{INSTRUMENT_BATCH_TYPE_LABELS[batch.batchType]}</Field>
          <Field label={t("instruments.batches.party")}>
            {batch.counterpartyId ? (
              <Link
                to="/cari/$id"
                params={{ id: batch.counterpartyId }}
                className="text-primary underline-offset-4 hover:underline"
              >
                {batchParty(batch)}
              </Link>
            ) : (
              batchParty(batch)
            )}
          </Field>
          {batch.controlKey ? (
            <Field label={t("instruments.fields.controlKey")}>
              {CONTROL_KEYS[batch.controlKey as ControlKey]?.label ?? batch.controlKey}
            </Field>
          ) : null}
          <Field label={t("instruments.batches.total")}>
            <MoneyText value={batch.totalAmount} className="text-base font-semibold" />
          </Field>
          {batch.commissionAmount !== "0" ? (
            <Field label={t("instruments.fields.commission")}>
              <MoneyText value={batch.commissionAmount} />
            </Field>
          ) : null}
          {batch.expenseAmount !== "0" ? (
            <Field label={t("instruments.fields.expense")}>
              <MoneyText value={batch.expenseAmount} />
            </Field>
          ) : null}
          <Field label={t("instruments.batches.ledgerStatus")}>
            <span className="flex flex-wrap items-center gap-2">
              <LedgerStatusBadge status={batch.ledgerStatus} />
              {batch.journalEntryId ? (
                <Link
                  to="/muhasebe/fisler/$id"
                  params={{ id: batch.journalEntryId }}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {t("instruments.batch.openEntry")}
                </Link>
              ) : null}
            </span>
          </Field>
          <Field label={t("instruments.batches.createdBy")}>
            {batch.createdBy ?? "—"} · <span className="tabular">{formatDateTime(batch.createdAt)}</span>
          </Field>
          <div className="grid gap-0.5 sm:col-span-2 lg:col-span-4">
            <dt className="text-xs text-muted-foreground">{t("instruments.fields.description")}</dt>
            <dd className="text-sm">{batch.description}</dd>
          </div>
        </dl>
      </Card>

      <section aria-labelledby={headingId} className="grid gap-2">
        <h2 id={headingId} className="text-base font-semibold">
          {t("instruments.batch.lines")}
        </h2>
        <SimpleTable
          caption={t("instruments.batch.lines")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                #
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.fields.portfolioNo")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.fields.kind")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.fields.serialNo")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.fields.drawer")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.fields.dueDate")}
              </th>
              <th scope="col" className={thClass}>
                {t("instruments.detail.transition")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("instruments.fields.amount")}
              </th>
            </tr>
          }
          foot={
            <tr>
              <td className={tdClass} colSpan={7}>
                {t("common.total")}
              </td>
              <td className={tdRightClass}>
                <MoneyText value={batch.totalAmount} />
              </td>
            </tr>
          }
        >
          {batch.lines.map((line) => (
            <tr key={line.lineNo} className="border-t">
              <td className={`${tdClass} tabular`}>{line.lineNo}</td>
              <td className={tdClass}>
                <Link
                  to="/cek-senet/$id"
                  params={{ id: line.instrumentId }}
                  className="font-mono text-xs font-semibold text-primary underline-offset-4 hover:underline"
                >
                  {line.portfolioNo}
                </Link>
              </td>
              <td className={tdClass}>{INSTRUMENT_KIND_LABELS[line.kind]}</td>
              <td className={`${tdClass} font-mono text-xs`}>{line.serialNo ?? "—"}</td>
              <td className={tdClass}>
                <span className="grid gap-0.5">
                  <span>{line.drawerName}</span>
                  {line.bankName ? (
                    <span className="text-xs text-muted-foreground">{line.bankName}</span>
                  ) : null}
                </span>
              </td>
              <td className={`${tdClass} tabular`}>{formatDate(line.dueDate)}</td>
              <td className={tdClass}>
                {line.fromStatus ? INSTRUMENT_STATUS_LABELS[line.fromStatus] : t("instruments.detail.new")}{" "}
                <span aria-hidden="true">→</span>
                <span className="sr-only">{t("instruments.detail.to")}</span>{" "}
                {INSTRUMENT_STATUS_LABELS[line.toStatus]}
              </td>
              <td className={tdRightClass}>
                <MoneyText value={line.amount} />
              </td>
            </tr>
          ))}
        </SimpleTable>
        {batch.status === "CANCELLED" ? (
          <p className="text-xs text-muted-foreground">
            <StatusBadge tone="neutral">{t("instruments.batches.cancelled")}</StatusBadge>{" "}
            {t("instruments.batch.cancelledHint")}
          </p>
        ) : null}
      </section>
    </div>
  );
}
